import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import { useProfile } from '@/hooks/useProfile';
import ApperIcon from '@/components/ApperIcon';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Busy, ConfirmDelete, DetailRow, PageHeader, Panel, StatusBadge } from '@/components/kit';
import { ListBody } from '@/components/inputs';
import { BackLink, recordGate } from '@/components/record';
import CopyButton from '@/components/admin/CopyButton';
import { MakeAdminButton } from '@/components/admin/MakeAdminDialog';
import DataTable, { Dash, RowLink } from '@/components/admin/DataTable';
import { CostCell, totalCosts, useCostEditor, useOrderCosts } from '@/components/admin/OrderCosts';
import { BOOL_STYLES, ORDER_STATUS_STYLES } from '@/shared/constants';
import { CUSTOMER_FIELDS, ORDER_LIST_FIELDS } from '@/shared/fields';
import { credits, signedMoney } from '@/shared/ledger';
import { money, orderTotal, planSummary, timeLeft } from '@/shared/pricing';
import { committed, rows, toArray } from '@/shared/data';
import { recountReferrals } from '@/shared/discounts';
import { formatLocalDate, DATE_FORMATS } from '@/utils/date';

const { eq } = window.ApperSDK.CoreSDK;

export const route = { path: '/admin/customers/:id', layout: 'owner', access: "['Admin_c','Staff_c'].includes(profile)" };

function orderColumns(costs, editCost) {
  return [
    {
      label: 'Order',
      render: (o) => (
        <RowLink to={`/admin/orders/${o.Id}`} mono sub={planSummary(o)}>
          {o.Name}
        </RowLink>
      ),
    },
    { label: 'Status', render: (o) => <StatusBadge value={o.status_c} styles={ORDER_STATUS_STYLES} /> },
    {
      label: 'Term ends',
      className: 'hidden md:table-cell',
      render: (o) =>
        o.ends_at_c ? (
          <span>
            {formatLocalDate(o.ends_at_c, DATE_FORMATS.SHORT)}
            <span className="block text-xs text-muted-foreground">{timeLeft(o.ends_at_c)}</span>
          </span>
        ) : (
          <Dash />
        ),
    },
    {
      label: 'Credits / cost',
      render: (o) => (
        <CostCell order={o} summary={costs.data?.[o.Id]} loading={costs.loading} onEdit={editCost} className="-ml-2" />
      ),
    },
    { label: 'Total', className: 'text-right tabular-nums', render: (o) => money(orderTotal(o)) },
  ];
}

function CostTotal({ label, value, tone }) {
  return (
    <div className="rounded-md border border-border bg-muted/30 px-3 py-2">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`mt-0.5 font-semibold tabular-nums ${tone ?? ''}`}>{value}</p>
    </div>
  );
}

export default function AdminCustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { profile } = useProfile();

  const { data: customer, loading, error, run } = useFetch(
    () => sdk.table('customers_c').select(CUSTOMER_FIELDS).get(id).then((r) => r.data ?? null),
    [id],
  );

  const orders = useFetch(
    () =>
      sdk
        .table('orders_c')
        .select(ORDER_LIST_FIELDS)
        .where(eq('customer_c', id))
        .orderByDesc('CreatedOn')
        .limit(50)
        .fetch()
        .then(rows),
    [id],
  );

  const [recounting, setRecounting] = useState(false);
  const orderList = orders.data ?? [];
  const costs = useOrderCosts(orderList.map((o) => o.Id));
  const [editCost, costDialog] = useCostEditor(() => costs.run());

  const back = <BackLink to="/admin/customers" label="Back to Customers" />;
  const gate = recordGate({ back, loading, record: customer, error, run, what: 'Customer' });
  if (gate) return gate;

  const tags = toArray(customer.tags_c);
  const newOrder = `/admin/orders/new?customer=${customer.Id}`;
  const totals = totalCosts(costs.data);
  const kept = totals.collected - totals.cost;

  async function recount() {
    setRecounting(true);
    try {
      const n = await recountReferrals(customer);
      toast.success(`${n} ${n === 1 ? 'referral' : 'referrals'} counted`);
      run();
    } catch (err) {
      toast.error(err?.message || 'Could not recount referrals');
    } finally {
      setRecounting(false);
    }
  }

  async function remove() {
    const res = await sdk.table('customers_c').remove(customer.Id);
    if (!committed(res, 'Customer deleted')) return false;
    navigate('/admin/customers');
    return true;
  }

  return (
    <div className="mx-auto max-w-6xl">
      {back}
      <PageHeader
        eyebrow={`Customer · since ${formatLocalDate(customer.CreatedOn, DATE_FORMATS.SHORT)}`}
        title={customer.Name}
        description={customer.email_c}
        actions={
          <>
            <Button size="sm" asChild>
              <Link to={newOrder}>
                <ApperIcon name="Plus" size={16} />
                New order
              </Link>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <Link to={`/admin/customers/${customer.Id}/edit`}>
                <ApperIcon name="Pencil" size={16} />
                Edit
              </Link>
            </Button>
            {profile === 'Admin_c' && <MakeAdminButton email={customer.email_c} name={customer.Name} />}
            <ConfirmDelete
              title={`Delete ${customer.Name}?`}
              description="Their orders stay but lose the link to this customer. This cannot be undone."
              onConfirm={remove}
            />
          </>
        }
      />

      <div className="grid gap-6 lg:grid-cols-2">
        <Panel title="Contact" icon="Radio">
          <dl className="divide-y divide-border">
            <DetailRow label="Name">{customer.Name}</DetailRow>
            <DetailRow label="Email">
              {customer.email_c ? (
                <a href={`mailto:${customer.email_c}`} className="text-primary hover:underline">
                  {customer.email_c}
                </a>
              ) : null}
            </DetailRow>
            <DetailRow label="Verified Email">
              <span className="flex flex-wrap items-center gap-2">
                <StatusBadge value={customer.email_verified_c ? 'Yes' : 'No'} styles={BOOL_STYLES} />
                {customer.email_verified_c && customer.email_verified_at_c && (
                  <span className="text-xs text-muted-foreground">
                    {formatLocalDate(customer.email_verified_at_c, DATE_FORMATS.SHORT)}
                  </span>
                )}
              </span>
            </DetailRow>
            <DetailRow label="Contact method">{customer.contact_method_c}</DetailRow>
            <DetailRow label="Handle">{customer.contact_handle_c}</DetailRow>
            <DetailRow label="Referral code">
              {customer.referral_code_c ? (
                <span className="flex items-center justify-between gap-2">
                  <span className="font-mono">{customer.referral_code_c}</span>
                  <CopyButton text={customer.referral_code_c} label="Referral code" size="sm" variant="ghost" />
                </span>
              ) : null}
            </DetailRow>
            <DetailRow label="Referrals earned">
              <span className="flex items-center justify-between gap-2">
                <span className="font-medium tabular-nums text-highlight">{Number(customer.referrals_earned_c || 0)}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={recount}
                  disabled={recounting}
                  title="Count live orders placed with their REF code or a discount code linked to them"
                >
                  {!recounting && <ApperIcon name="RefreshCw" size={16} />}
                  <Busy busy={recounting} label="Recount" busyLabel="Counting…" />
                </Button>
              </span>
            </DetailRow>
          </dl>
        </Panel>

        <Panel title="Preferences" icon="MailCheck">
          <dl className="divide-y divide-border">
            <DetailRow label="Promotions">
              <StatusBadge value={customer.marketing_opt_in_c ? 'Yes' : 'No'} styles={BOOL_STYLES} />
            </DetailRow>
            <DetailRow label="Expiry reminders">
              <StatusBadge value={customer.expiry_reminders_c ? 'Yes' : 'No'} styles={BOOL_STYLES} />
            </DetailRow>
            <DetailRow label="Unsubscribed">
              {customer.unsubscribed_at_c ? formatLocalDate(customer.unsubscribed_at_c, DATE_FORMATS.WITH_TIME) : null}
            </DetailRow>
            <DetailRow label="Tags">
              {tags.length ? (
                <span className="flex flex-wrap gap-1">
                  {tags.map((t) => (
                    <Badge key={t} variant="outline">
                      {t}
                    </Badge>
                  ))}
                </span>
              ) : null}
            </DetailRow>
          </dl>
        </Panel>

        <Panel title="Credits and cost" icon="Coins" className="lg:col-span-2" bodyClassName="py-3">
          {orders.loading || (costs.loading && !Object.keys(costs.data ?? {}).length) ? (
            <p className="text-sm text-muted-foreground">Adding up their orders…</p>
          ) : costs.error ? (
            <p className="text-sm text-destructive">Could not load credits and cost. Reload the page to try again.</p>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <CostTotal label="Credits used" value={credits(totals.credits)} />
                <CostTotal label="Cost to you" value={signedMoney(totals.cost)} />
                <CostTotal label="Collected" value={signedMoney(totals.collected)} />
                <CostTotal
                  label="Kept"
                  value={signedMoney(kept)}
                  tone={kept >= 0 ? 'text-success' : 'text-destructive'}
                />
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                {orderList.length
                  ? 'Across all their orders. Click credits or cost on an order below to change them.'
                  : 'Nothing yet. Credits and cost show up here once they have an order.'}
              </p>
            </>
          )}
        </Panel>

        <Panel title="Notes" icon="NotebookPen" className="lg:col-span-2" bodyClassName="py-3">
          {customer.notes_c ? (
            <p className="whitespace-pre-wrap text-sm">{customer.notes_c}</p>
          ) : (
            <p className="text-sm text-muted-foreground">No notes yet. Only the team sees these.</p>
          )}
        </Panel>
      </div>

      <section className="mt-8">
        <h2 className="mb-3 font-heading text-2xl">Orders</h2>
        <ListBody
          loading={orders.loading}
          data={orders.data}
          error={orders.error}
          run={orders.run}
          rows={3}
          isEmpty={!orderList.length}
          empty={{
            icon: 'Package',
            title: 'No orders yet',
            description: 'Take one by hand and it is linked to this customer.',
            action: () => navigate(newOrder),
            actionLabel: 'New order',
          }}
        >
          <DataTable columns={orderColumns(costs, editCost)} rows={orderList} />
        </ListBody>
      </section>
      {costDialog}
    </div>
  );
}
